import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MessageCircle, Info, Calendar as CalendarIcon, TrendingDown, RefreshCw } from 'lucide-react';
import { whatsappNumber, lastMinuteDiscount } from '../data/mockData';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isBefore, startOfDay } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { useAvailability } from '../contexts/AvailabilityContext';

export const AvailabilityHeatmap = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const { t } = useLanguage();
  const { isDateBooked, lastSync, loading, pricingSeasons, getPriceForDate } = useAvailability();

  // Last minute deals: 5-10 days from now
  const lastMinuteDates = Array.from({ length: 6 }, (_, i) => addDays(new Date(), 5 + i));

  const getDateStatus = (date) => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);

    if (isBefore(checkDate, today)) return 'past';
    if (isDateBooked(date)) return 'booked';

    const isLastMinute = lastMinuteDates.some(
      lmDate => startOfDay(lmDate).getTime() === checkDate.getTime()
    );
    if (isLastMinute) return 'last-minute';

    return 'available';
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'd like to check detailed availability and pricing for Villa in Pietra. Could you help me find the perfect dates?");
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const generateCalendarDays = () => {
    const start = startOfMonth(selectedMonth);
    const end = endOfMonth(selectedMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = start.getDay();
    const paddingDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const paddingStart = Array(paddingDays).fill(null);
    return [...paddingStart, ...days];
  };

  const changeMonth = (offset) => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setSelectedMonth(newMonth);
  };

  const calendarDays = generateCalendarDays();
  const weekdays = t('availability.weekdays');

  return (
    <section className="availability-heatmap-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <div className="section-badge">{t('availability.badge')}</div>
          <h2 className="section-title">{t('availability.title')}</h2>
          <p className="section-description">{t('availability.description')}</p>
        </div>

        <div className="heatmap-container">
          <Card className="heatmap-card">
            <CardHeader className="heatmap-header">
              <div className="heatmap-nav">
                <Button onClick={() => changeMonth(-1)} variant="outline" size="sm">
                  {t('availability.previous')}
                </Button>
                <CardTitle className="heatmap-month-title">
                  {format(selectedMonth, 'MMMM yyyy')}
                </CardTitle>
                <Button onClick={() => changeMonth(1)} variant="outline" size="sm">
                  {t('availability.next')}
                </Button>
              </div>
            </CardHeader>

            <CardContent className="heatmap-content">
              <div className="heatmap-legend">
                <div className="legend-item">
                  <span className="legend-dot available"></span>
                  <span>{t('availability.available')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot last-minute"></span>
                  <span>{t('availability.lastMinute')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot booked"></span>
                  <span>{t('availability.booked')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot past"></span>
                  <span>{t('availability.past')}</span>
                </div>
              </div>

              <div className="heatmap-calendar">
                <div className="heatmap-weekdays">
                  {Array.isArray(weekdays) && weekdays.map(day => (
                    <div key={day} className="heatmap-weekday">{day}</div>
                  ))}
                </div>

                <div className="heatmap-days">
                  {loading ? (
                    <div className="heatmap-loading">
                      <RefreshCw className="h-6 w-6 animate-spin" />
                    </div>
                  ) : (
                    calendarDays.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} className="heatmap-day empty"></div>;
                      }

                      const status = getDateStatus(date);
                      const price = getPriceForDate(date);
                      const discount = status === 'last-minute' ? (1 - lastMinuteDiscount) : 1;
                      const finalPrice = Math.round(price * discount);

                      return (
                        <div
                          key={date.toISOString()}
                          className={`heatmap-day ${status} ${isToday(date) ? 'today' : ''}`}
                          onMouseEnter={() => setHoveredDate(date)}
                          onMouseLeave={() => setHoveredDate(null)}
                          data-testid={`day-${format(date, 'yyyy-MM-dd')}`}
                        >
                          <span className="day-number">{format(date, 'd')}</span>
                          {status === 'available' || status === 'last-minute' ? (
                            <span className="day-price">
                              €{finalPrice}
                              {status === 'last-minute' && <TrendingDown className="discount-icon" />}
                            </span>
                          ) : null}
                          {status === 'booked' && <span className="day-label">{t('availability.booked')}</span>}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {hoveredDate && getDateStatus(hoveredDate) !== 'past' && getDateStatus(hoveredDate) !== 'booked' && (
                <div className="heatmap-hover-info">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    <strong>{format(hoveredDate, 'MMMM d, yyyy')}</strong>
                    {' '}- €{Math.round(getPriceForDate(hoveredDate) * (getDateStatus(hoveredDate) === 'last-minute' ? (1 - lastMinuteDiscount) : 1))} {t('availability.perNight')}
                    {getDateStatus(hoveredDate) === 'last-minute' && ` (10% ${t('availability.off')})`}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="pricing-info-card">
            <CardHeader>
              <CardTitle>{t('availability.seasonalPricing')}</CardTitle>
            </CardHeader>
            <CardContent className="pricing-info-content">
              {pricingSeasons.map((season, i) => {
                const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const tierClass = season.rate >= 800 ? 'high' : season.rate >= 500 ? 'mid' : 'low';
                return (
                  <div key={i} className={`season-price-item ${tierClass}`}>
                    <div className="season-label">
                      <span className="season-dot"></span>
                      {season.name}
                    </div>
                    <div className="season-details">
                      <span className="season-price">€{season.rate}</span>
                      <span className="season-months">
                        {MONTHS[season.start_month]} {season.start_day} – {MONTHS[season.end_month]} {season.end_day}
                      </span>
                    </div>
                  </div>
                );
              })}

              <div className="pricing-divider"></div>

              <div className="pricing-notes">
                <Info className="h-4 w-4" />
                <div>
                  <p><strong>{t('availability.deposit')}</strong> €500 {t('availability.atCheckin')}</p>
                  <p><strong>{t('availability.cleaningFee')}</strong> €300</p>
                  <p><strong>{t('availability.lastMinuteLabel')}</strong> {Math.round(lastMinuteDiscount * 100)}% {t('availability.offBookings')}</p>
                </div>
              </div>

              <Button
                onClick={handleWhatsAppClick}
                className="whatsapp-button-full"
                size="lg"
                data-testid="request-quote-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                {t('availability.requestQuote')}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="heatmap-disclaimer">
          <Info className="h-5 w-5" />
          <div>
            <p>{t('availability.disclaimer')}</p>
            {lastSync && (
              <p className="sync-info">
                {t('availability.syncedAt')} {new Date(lastSync).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
